import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { SERVICE_OPTIONS } from "../data/services";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

export default function InquiryForm({ defaultService }) {
  const [form, setForm] = useState({ ...EMPTY, service: defaultService || "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in name, email, phone and select a service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm({ ...EMPTY });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="lead-name">Full Name</Label>
          <Input
            id="lead-name"
            data-testid="inquiry-name-input"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-phone">Phone</Label>
          <Input
            id="lead-phone"
            data-testid="inquiry-phone-input"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+91 ..."
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="lead-email">Email</Label>
        <Input
          id="lead-email"
          type="email"
          data-testid="inquiry-email-input"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label>Service of Interest</Label>
        <Select value={form.service} onValueChange={(v) => update("service", v)}>
          <SelectTrigger data-testid="inquiry-service-select">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`inquiry-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="lead-message">Message</Label>
        <Textarea
          id="lead-message"
          data-testid="inquiry-message-input"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us about your requirement..."
          rows={4}
        />
      </div>

      <Button
        type="submit"
        data-testid="inquiry-submit-btn"
        disabled={submitting}
        className="w-full rounded-full h-12 text-base"
      >
        {submitting ? "Sending..." : "Request a Callback"}
      </Button>
    </form>
  );
}
